import { PollyClient, SynthesizeSpeechCommand } from "@aws-sdk/client-polly";
import { AWS_CONFIG } from "../config/aws-config";

const pollyClient = new PollyClient(AWS_CONFIG);
let currentAudio = null;
let currentAudioData = null;
let currentPosition = 0;

// Amazon Polly limits: 
// - Plain text: 3000 characters
// - SSML: 6000 characters total (including all tags and whitespace)
// However, some accounts/regions may have stricter limits
// Using very conservative limits to ensure compatibility
const MAX_SSML_TOTAL_LENGTH = 2500; // Very conservative limit (some accounts may have 3000 limit even for SSML)
const MAX_SSML_CONTENT_LENGTH = 2450; // Content only (leaving room for <speak></speak> tags)
const MAX_PLAIN_TEXT_LENGTH = 3000;

const POLLY_VOICES = {
  en: {
    voiceId: "Joanna",
    languageCode: "en-US",
    engine: "neural"
  },
  ja: {
    voiceId: "Kazuha", 
    languageCode: "ja-JP",
    engine: "neural"
  },
  de: {
    voiceId: "Vicki",
    languageCode: "de-DE", 
    engine: "neural"
  },
  fr: {
    voiceId: "Lea",
    languageCode: "fr-FR",
    engine: "neural"
  },
  es: {
    voiceId: "Lupe",
    languageCode: "es-US",
    engine: "neural"
  },
  it: {
    voiceId: "Bianca",
    languageCode: "it-IT",
    engine: "neural"
  }
};

/**
 * Splits text into chunks that respect Amazon Polly's character limits
 * Tries to split at sentence boundaries when possible
 */
const splitTextIntoChunks = (text, maxLength) => {
  if (!text || text.length === 0) {
    return [''];
  }
  
  if (text.length <= maxLength) {
    return [text];
  }

  const chunks = [];
  let remainingText = text.trim();

  while (remainingText.length > 0) {
    if (remainingText.length <= maxLength) {
      chunks.push(remainingText);
      break;
    }

    // Try to split at sentence boundaries (., !, ?, \n)
    let splitIndex = maxLength;
    const sentenceEnders = /[.!?\n]/g;
    let lastMatch = -1;
    let match;
    
    // Reset regex lastIndex
    sentenceEnders.lastIndex = 0;

    // Find the last sentence boundary before maxLength
    while ((match = sentenceEnders.exec(remainingText)) !== null) {
      if (match.index > maxLength) break;
      lastMatch = match.index;
    }

    // If we found a sentence boundary, use it; otherwise try word boundary
    if (lastMatch > maxLength * 0.5) {
      // Only use sentence boundary if it's at least halfway through
      splitIndex = lastMatch + 1;
    } else {
      // Try to split at word boundaries
      const wordBoundary = remainingText.lastIndexOf(' ', maxLength);
      if (wordBoundary > maxLength * 0.5) {
        splitIndex = wordBoundary + 1;
      } else {
        // Force split at maxLength if no good boundary found
        splitIndex = maxLength;
      }
    }

    const chunk = remainingText.substring(0, splitIndex).trim();
    if (chunk.length > 0) {
      chunks.push(chunk);
    }
    remainingText = remainingText.substring(splitIndex).trim();
    
    // Safety check to prevent infinite loop
    if (chunk.length === 0 && remainingText.length > 0) {
      // Force a split to prevent infinite loop
      const forcedChunk = remainingText.substring(0, maxLength);
      chunks.push(forcedChunk);
      remainingText = remainingText.substring(maxLength).trim();
    }
  }

  return chunks;
};

/**
 * Synthesizes a single chunk of text
 */
const synthesizeChunk = async (text, voiceConfig) => {
  const command = new SynthesizeSpeechCommand({
    Engine: voiceConfig.engine,
    OutputFormat: "mp3",
    Text: text,
    VoiceId: voiceConfig.voiceId,
    TextType: "ssml",
    LanguageCode: voiceConfig.languageCode,
    Pitch: "+10%",
    SpeakingRate: "0.95",
  });

  const response = await pollyClient.send(command);
  const audioData = await response.AudioStream.transformToByteArray();
  return audioData;
};

export const synthesizeSpeech = async (text, isSSML = false, languageCode = 'en') => {
  try {
    const voiceConfig = POLLY_VOICES[languageCode] || POLLY_VOICES.en;

    // If it's not SSML, wrap it in speak tags
    let finalText = isSSML ? text : `<speak>${text}</speak>`;

    // Normalize SSML: remove extra whitespace/newlines inside <speak> tags to minimize character count
    // Extract content, trim it, then rebuild SSML without extra whitespace
    const speakMatch = finalText.match(/<speak>([\s\S]*?)<\/speak>/);
    if (speakMatch) {
      const content = speakMatch[1].trim().replace(/\s+/g, ' '); // Normalize whitespace
      finalText = `<speak>${content}</speak>`;
    }

    const totalLength = finalText.length;
    
    // Always check and chunk if needed, even if slightly under limit to be safe
    if (totalLength <= MAX_SSML_TOTAL_LENGTH) {
      // Text is within limits, try processing normally
      // But if it fails, we'll retry with chunking
      try {
        const command = new SynthesizeSpeechCommand({
          Engine: voiceConfig.engine,
          OutputFormat: "mp3",
          Text: finalText,
          VoiceId: voiceConfig.voiceId,
          TextType: "ssml",
          LanguageCode: voiceConfig.languageCode,
          Pitch: "+10%",
          SpeakingRate: "0.95",
        });

        const response = await pollyClient.send(command);
        const audioData = await response.AudioStream.transformToByteArray();
        currentAudioData = audioData;
        return audioData;
      } catch (apiError) {
        // If API fails with length error even though we're under limit, chunk it anyway
        if (apiError.name === 'TextLengthExceededException' || 
            apiError.message?.includes('Maximum text length') ||
            apiError.message?.includes('text length')) {
          console.log(`API rejected text even at ${totalLength} chars. Forcing chunking...`);
          // Fall through to chunking logic
        } else {
          throw apiError;
        }
      }
    }
    
    // Text exceeds limits or API rejected it, split into chunks
    // Extract content between <speak> tags for chunking
    const speakTagMatch = finalText.match(/<speak>([\s\S]*?)<\/speak>/);
    let contentText = speakTagMatch ? speakTagMatch[1] : finalText;
    
    // If no match, try to extract content differently
    if (!speakTagMatch && finalText.includes('<speak>')) {
      const startIdx = finalText.indexOf('<speak>') + 7;
      const endIdx = finalText.lastIndexOf('</speak>');
      if (endIdx > startIdx) {
        contentText = finalText.substring(startIdx, endIdx);
      }
    }
    
    console.log(`Text length (${totalLength} total, ${contentText.length} content) exceeds limit (${MAX_SSML_TOTAL_LENGTH}). Splitting into chunks...`);
    
    const contentChunks = splitTextIntoChunks(contentText, MAX_SSML_CONTENT_LENGTH);
    console.log(`Split into ${contentChunks.length} chunks. Chunk sizes:`, contentChunks.map(c => c.length));
    
    const audioChunks = [];

    // Process each chunk sequentially
    for (let i = 0; i < contentChunks.length; i++) {
      // Create clean SSML for each chunk (no extra whitespace)
      const chunkText = `<speak>${contentChunks[i]}</speak>`;
      const chunkLength = chunkText.length;
      
      // Double-check chunk length before sending
      if (chunkLength > MAX_SSML_TOTAL_LENGTH) {
        console.warn(`Chunk ${i + 1} is still too long (${chunkLength} chars). Splitting further...`);
        // Split this chunk even more aggressively
        const subChunks = splitTextIntoChunks(contentChunks[i], Math.floor(MAX_SSML_CONTENT_LENGTH * 0.8));
        for (const subChunk of subChunks) {
          const subChunkText = `<speak>${subChunk}</speak>`;
          console.log(`Processing sub-chunk (${subChunkText.length} chars)`);
          const audioChunk = await synthesizeChunk(subChunkText, voiceConfig);
          audioChunks.push(audioChunk);
        }
      } else {
        console.log(`Processing chunk ${i + 1}/${contentChunks.length} (${chunkLength} chars)`);
        const audioChunk = await synthesizeChunk(chunkText, voiceConfig);
        audioChunks.push(audioChunk);
      }
    }

    // Combine all audio chunks into a single Uint8Array
    // Note: Simple concatenation works for MP3 files in most cases
    const combinedLength = audioChunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const combinedAudio = new Uint8Array(combinedLength);
    let offset = 0;
    
    for (const chunk of audioChunks) {
      combinedAudio.set(chunk, offset);
      offset += chunk.length;
    }

    currentAudioData = combinedAudio;
    return combinedAudio;
  } catch (error) {
    console.error("Error synthesizing speech:", error);
    console.error("Error details:", {
      name: error.name,
      message: error.message,
      code: error.$metadata?.httpStatusCode
    });
    
    // Provide more detailed error information
    if (error.name === 'TextLengthExceededException' || 
        error.name === 'ValidationException' || 
        error.message?.includes('Maximum text length') ||
        error.message?.includes('text length')) {
      console.error("Text length validation error. Attempting to retry with smaller chunks...");
      // Could implement retry logic here if needed
    } else if (error.name === 'UnrecognizedClientException' || error.message?.includes('Missing Authentication Token')) {
      console.error("Authentication error. Please check AWS credentials in aws-config.js");
    }
    
    throw error;
  }
};

export const playAudio = (audioData) => {
  return new Promise((resolve, reject) => {
    try {
      // Stop any existing audio
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }

      const audioBlob = new Blob([audioData], { type: "audio/mpeg" });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      currentAudio = audio;

      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        currentAudio = null;
        currentPosition = 0;
        resolve();
      };

      audio.onerror = (error) => {
        console.error("Audio playback error:", error);
        URL.revokeObjectURL(audioUrl);
        currentAudio = null;
        currentPosition = 0;
        reject(error);
      };

      audio.play().catch((error) => {
        console.error("Error playing audio:", error);
        reject(error);
      });
    } catch (error) {
      console.error("Error in playAudio:", error);
      reject(error);
    }
  });
};

export const pauseCurrentAudio = () => {
  if (currentAudio) {
    currentPosition = currentAudio.currentTime;
    currentAudio.pause();
  }
};

export const resumeCurrentAudio = () => {
  if (currentAudio && currentAudioData) {
    const audioBlob = new Blob([currentAudioData], { type: "audio/mpeg" });
    const audioUrl = URL.createObjectURL(audioBlob);
    const audio = new Audio(audioUrl);
    currentAudio = audio;

    audio.currentTime = currentPosition;
    audio.load();
    audio.play().catch((error) => {
      console.error("Error resuming audio:", error);
    });
  }
};

export const stopCurrentAudio = () => {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.currentTime = 0;
    currentAudio = null;
    currentPosition = 0;
    currentAudioData = null;
  }
};
